import { languageNames, supportedLanguages } from '@/i18n/languages';
import type { SharedData } from '@/types';
import { router, usePage } from '@inertiajs/react';
import type { TOptions } from 'i18next';
import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * Custom translation hook that provides a more convenient API
 * for our application's translation needs.
 */
export const useTranslation = (namespace?: string) => {
    const { t: i18nT, i18n } = useI18nTranslation(namespace);
    const { auth, locale } = usePage<SharedData>().props;

    /**
     * Translation function with better TypeScript support and fallbacks
     */
    const t = (key: string, options?: TOptions): string => {
        try {
            const translated = i18nT(key, options) as string;
            // If translation is the same as the key, it means it wasn't found
            if (translated === key && !key.includes('.')) {
                // For simple keys without namespace, try common namespace
                const commonTranslation = i18nT(`common.${key}`, options) as string;
                if (commonTranslation !== `common.${key}`) {
                    return commonTranslation;
                }
            }
            return translated;
        } catch (error) {
            console.warn(`Translation error for key "${key}":`, error);
            return key;
        }
    };

    /**
     * Change language and persist the choice
     */
    const changeLanguage = async (lng: string) => {
        try {
            const destination = locale?.urls?.[lng];

            if (!destination) {
                throw new Error(`No localized URL is available for "${lng}"`);
            }

            // Update user preference on server
            if (!auth?.user) {
                router.visit(destination, { preserveScroll: true });
            } else {
                router.post(
                    route('locale.update'),
                    { locale: lng },
                    {
                        preserveScroll: true,
                        onError: () => {
                            console.warn('Failed to update locale on server');
                        },
                    },
                );
            }
        } catch (error) {
            console.error('Failed to change language:', error);
        }
    };

    /**
     * Get current language
     */
    const currentLanguage = i18n.language || 'pt';

    /**
     * Get available languages
     */
    const availableLanguages = locale?.available || supportedLanguages;

    /**
     * Check if a specific language is available
     */
    const isLanguageAvailable = (lng: string) => availableLanguages.includes(lng);

    /**
     * Get language display name
     */
    const getLanguageName = (lng: string) => {
        return languageNames[lng] || lng;
    };

    return {
        t,
        i18n,
        currentLanguage,
        availableLanguages,
        changeLanguage,
        isLanguageAvailable,
        getLanguageName,
        ready: i18n.isInitialized,
    };
};

export default useTranslation;
